/*
 * One place for the money on a reconciliation.
 *
 * These figures — what a shift was expected to hold, what was banked, what
 * is unaccounted for — were written out separately in the site's own
 * reconciliation form, the daily director view, the weekly director view
 * and the submitted-report dialog. Four copies of the same arithmetic, and
 * they drifted:
 *
 *   - The manual paid in/out was added to the variance in three of them and
 *     to the cash to bank in none, so a £20 paid in read as £20 unaccounted
 *     for and never reached the banking total.
 *   - The jetwash weekday rule lived in three files and had to be changed in
 *     all three or the director's Total Cash to Bank stopped matching the
 *     site's.
 *   - The report dialog's till figures summed to £24.91 while the headline
 *     at the top of the same page said £4.91.
 *
 * None of that was careless: there was simply nowhere to put a rule so that
 * everybody read the same one. This is that place.
 *
 * The readers below are deliberately tolerant. The same field arrives as
 * `pouchAmounts` from the form, `pouchAmount` from an older record and
 * `pouch` from an aggregation, and each of the four copies knew about a
 * different subset. Accepting all of them is what lets one function serve
 * every caller.
 */

/** A number, whatever shape it arrives in. Blank and rubbish both read 0. */
export function money(value) {
  if (value === null || value === undefined || value === '') return 0;

  const parsed =
    typeof value === 'number' ? value : Number.parseFloat(String(value).replace(/[^0-9.-]/g, ''));

  return Number.isFinite(parsed) ? parsed : 0;
}

/** The first key that holds something. */
function first(source, keys) {
  for (const key of keys) {
    const value = source?.[key];
    if (value !== null && value !== undefined && value !== '') return value;
  }

  return 0;
}

/** What the till said it should hold. */
export function expectedCash(shift) {
  return money(first(shift, ['totalCashValue', 'totalCash', 'cashValue']));
}

/** Cash sent to the pouch. */
export function pouch(shift) {
  return money(first(shift, ['pouchAmounts', 'pouchAmount', 'pouch']));
}

/** Cash dropped to the safe. */
export function safeDrop(shift) {
  return money(first(shift, ['submittedCash', 'safeDrop', 'cashSafeDrop', 'bankedCash']));
}

/**
 * Money paid into or out of the till that is not a sale.
 *
 * Positive is money in, negative is money out. It moves the banking and
 * never the variance — see cashToBank() and variance() below.
 */
export function manualPaidInOut(shift) {
  return money(
    first(shift, ['manualPaidInOut', 'manualPaidInOutValue', 'manualPaid', 'manualAdjustment'])
  );
}

/**
 * Drive-offs and no-means-of-payment.
 *
 * Both are cash the till expected and did not get for a known reason, so
 * they are added back before the variance is judged.
 */
export function exceptions(shift) {
  const ex = shift?.exceptions || {};
  const nmop = ex.noMeansOfPayment || ex.nmop || {};
  const driveOff = ex.driveOff || ex.driveOffs || {};

  const nmopAmount = money(nmop.amount);
  const driveOffAmount = money(driveOff.amount);

  return {
    nmopAmount,
    driveOffAmount,
    nmopQty: money(nmop.quantity ?? nmop.count ?? (nmop.reported ? 1 : 0)),
    driveOffQty: money(driveOff.quantity ?? driveOff.count ?? (driveOff.reported ? 1 : 0)),
    total: nmopAmount + driveOffAmount,
  };
}

/**
 * What is unaccounted for on one shift.
 *
 *   pouch + safe drop + exceptions + manual paid in/out − expected
 *
 * The manual paid in/out is in here because the site's own sheet puts it here,
 * and the sheet is the thing this system has to agree with. A paid-out is cash
 * that left the drawer against the till's expectation, so the drawer is short
 * by exactly that much and the variance has to carry it — otherwise the shift
 * reads as £50.03 astray when it is nothing of the kind.
 *
 * It was taken out of here and put into cash to bank on the reasoning that
 * money paid in or out is "accounted for". That reasoning is tidy and wrong,
 * and it moved the same £50.03 out of the one column that explains it and into
 * the one figure that must not move: Friday 18-Sep came out as a £47.25
 * variance against the sheet's −£2.78, and banking of £6,625.27 against the
 * sheet's £6,675.30.
 */
export function variance(shift) {
  return (
    pouch(shift) +
    safeDrop(shift) +
    exceptions(shift).total +
    manualPaidInOut(shift) -
    expectedCash(shift)
  );
}

/**
 * What one shift sends to the bank.
 *
 *   pouch + safe drop
 *
 * The manual paid in/out is not here. What goes to the bank is what is in the
 * pouch and the safe drop — counted cash, already net of anything paid out of
 * the drawer — so adding the paid in/out again deducts it twice. On Friday
 * 18-Sep that turned £6,675.30 of banking into £6,625.27, exactly the £50.03
 * that had already left.
 *
 * The jetwash and the other machine takings are added on top of this by the
 * day, not by the shift, because they are counted once a week.
 */
export function cashToBank(shift) {
  return pouch(shift) + safeDrop(shift);
}

/* --------------------------------------------------------------------------
   Which record carries the weekly entries

   Banking runs a day behind: the float is counted on a Sunday morning, when
   the record being filed is Saturday's, and the jetwash cash, the weekly
   wastage and the five weekly documents all arrive on a Monday, when the
   record is Sunday's. Both tests name the RECORD's date, so each sits one
   day before the day the work is done.

   It is still one day a week for each, which is what stops a figure being
   banked twice.
   -------------------------------------------------------------------------- */

function weekday(value) {
  if (!value) return -1;

  const date = value instanceof Date ? value : new Date(`${String(value).slice(0, 10)}T12:00:00`);

  return Number.isNaN(date.getTime()) ? -1 : date.getDay();
}

/** Saturday's record — the float is counted the next morning. */
export function isFloatCountDay(value) {
  return weekday(value) === 6;
}

/** Sunday's record — jetwash, wastage and the weekly documents follow it. */
export function isWeeklyEntryDay(value) {
  return weekday(value) === 0;
}
